/**
 * Customer Migration Utility
 * Run this from your browser console or integrate into an admin page
 */

import { directSupabase } from '../lib/supabaseClient';

export async function migrateCustomersFromQuoteDock() {
  console.log('🚀 Starting customer migration from QuoteDock...');
  
  try {
    // Step 1: Fetch all QuoteDock customers directly from Edge Function
    console.log('📥 Fetching QuoteDock customers...');
    const accessToken = localStorage.getItem('sb-access-token');
    
    const response = await fetch('https://fggiylzoktmnjfslrpyw.supabase.co/functions/v1/client-customers', {
      headers: {
        'Authorization': accessToken ? `Bearer ${accessToken}` : '',
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch customers: ${response.statusText}`);
    }
    
    const data = await response.json();
    const customers = data.success ? data.data : [];
    console.log(`Found ${customers.length} QuoteDock customers`);
    
    // Step 2: Check for existing customers to avoid duplicates
    console.log('🔍 Checking for existing customers...');
    const { data: existingCustomers } = await directSupabase
      .from('customers')
      .select('email, phone_number');
    
    const existingEmails = new Set(existingCustomers?.map(c => c.email).filter(Boolean) || []);
    const existingPhones = new Set(existingCustomers?.map(c => c.phone_number).filter(Boolean) || []);
    
    // Step 3: Migrate each customer
    const migrationResults = [];
    
    for (const customer of customers) {
      try {
        console.log(`Migrating: ${customer.name} (ID: ${customer.id})`);
        
        // Check for duplicates
        const isDuplicateEmail = customer.email && existingEmails.has(customer.email);
        const isDuplicatePhone = customer.phone_number && existingPhones.has(customer.phone_number);
        
        if (isDuplicateEmail || isDuplicatePhone) {
          console.log(`⚠️ Skipping ${customer.name}: duplicate ${isDuplicateEmail ? 'email' : 'phone'}`);
          
          // Try to find existing customer and create mapping
          let existingCustomer = null;
          if (isDuplicateEmail) {
            const { data } = await directSupabase
              .from('customers')
              .select('id')
              .eq('email', customer.email)
              .single();
            existingCustomer = data;
          } else if (isDuplicatePhone) {
            const { data } = await directSupabase
              .from('customers')
              .select('id')
              .eq('phone_number', customer.phone_number)
              .single();
            existingCustomer = data;
          }
          
          if (existingCustomer) {
            // Create mapping for existing customer
            const { error: mappingError } = await directSupabase.rpc('create_customer_mapping', {
              quotedock_id: customer.id,
              jarvis_id: existingCustomer.id,
              name: customer.name,
              email: customer.email || null,
              phone: customer.phone_number || null
            });
            
            if (!mappingError) {
              migrationResults.push({
                quotedockId: customer.id,
                jarvisId: existingCustomer.id,
                name: customer.name,
                success: true,
                skipped: true
              });
              console.log(`✅ Mapped existing: ${customer.name} -> ${existingCustomer.id}`);
            } else {
              console.error(`❌ Failed to create mapping for existing ${customer.name}:`, mappingError.message);
            }
          }
          continue;
        }
        
        // Ensure phone number is not null (use placeholder if missing)
        const phoneNumber = customer.phone_number || 'No phone provided';
        
        // Create customer in JARVIS database
        const { data: newCustomer, error } = await directSupabase
          .from('customers')
          .insert({
            name: customer.name,
            first_name: customer.first_name,
            last_name: customer.last_name,
            phone_number: phoneNumber,
            email: customer.email,
            address: customer.address,
            billing_address: customer.billing_address,
            organization_id: customer.organization_id,
            organization_name: customer.organization_name,
            created_at: customer.created_at,
            updated_at: customer.updated_at
          })
          .select()
          .single();
          
        if (error) {
          console.error(`❌ Failed to create customer ${customer.name}:`, error.message);
          migrationResults.push({
            quotedockId: customer.id,
            name: customer.name,
            success: false,
            error: error.message
          });
          continue;
        }
        
        // Create mapping record using RPC function to bypass RLS
        const { error: mappingError } = await directSupabase.rpc('create_customer_mapping', {
          quotedock_id: customer.id,
          jarvis_id: newCustomer.id,
          name: customer.name,
          email: customer.email || null,
          phone: phoneNumber
        });
          
        if (mappingError) {
          console.error(`❌ Failed to create mapping for ${customer.name}:`, mappingError.message);
        }
        
        // Track emails/phones to avoid duplicates in this run
        if (customer.email) existingEmails.add(customer.email);
        if (phoneNumber) existingPhones.add(phoneNumber);
        
        migrationResults.push({
          quotedockId: customer.id,
          jarvisId: newCustomer.id,
          name: customer.name,
          success: true
        });
        
        console.log(`✅ Migrated: ${customer.name} -> ${newCustomer.id}`);
        
      } catch (error) {
        console.error(`❌ Error migrating ${customer.name}:`, error);
        migrationResults.push({
          quotedockId: customer.id,
          name: customer.name,
          success: false,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
    
    // Step 3: Update project references
    console.log('🔗 Updating project customer references...');
    const successfulMigrations = migrationResults.filter(r => r.success);
    
    for (const migration of successfulMigrations) {
      try {
        const { error } = await directSupabase
          .from('projects')
          .update({ customer_id: migration.jarvisId })
          .eq('customer_id', migration.quotedockId);
          
        if (error) {
          console.error(`❌ Failed to update projects for ${migration.name}:`, error.message);
        } else {
          console.log(`✅ Updated project references for ${migration.name}`);
        }
      } catch (error) {
        console.error(`❌ Error updating projects for ${migration.name}:`, error);
      }
    }
    
    // Summary
    const successful = migrationResults.filter(r => r.success).length;
    const failed = migrationResults.filter(r => !r.success).length;
    
    console.log('✅ Migration completed!');
    console.log(`📊 Summary:`);
    console.log(`   - Successful: ${successful}`);
    console.log(`   - Failed: ${failed}`);
    console.log(`   - Total: ${migrationResults.length}`);
    
    return {
      success: true,
      total: migrationResults.length,
      successful,
      failed,
      results: migrationResults
    };
    
  } catch (error) {
    console.error('❌ Migration failed:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Helper function to run from browser console
(window as any).migrateCustomers = migrateCustomersFromQuoteDock;
